import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { createForm } from 'rc-form';
import classnames from 'classnames';
import { Card, CardContent, Collapse, Modal, Paper } from '@material-ui/core';

import GuideListItemGeneralTabs from 'Console/components/applications/detail/guides/GuideListItemGeneralTabs';
import GuideListItemContent from 'Console/components/applications/detail/guides/GuideListItemContent';

import GuideFooter from './GuideFooter';
import PublishGuideForm from './PublishGuideForm';
import EditGuideForm from './EditGuideForm';

const styles = (theme) => ({
    guidesBox: {
        boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
        borderRadius: 6,
        marginBottom: theme.typography.pxToRem(25),
        overflow: 'visible',
        transition: 'background-color 1.5s ease',
        '&.newGuide': {
            backgroundColor: '#f4e2a1',
        },
    },
    CardContentroot: {
        '&:last-child': {
            paddingBottom: theme.typography.pxToRem(6),
        },
    },
    publishForm: {
        marginTop: theme.typography.pxToRem(10),
        marginBottom: theme.typography.pxToRem(35),
    },
    modalPaper: {
        position: 'absolute',
        width: theme.typography.pxToRem(800),
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: theme.spacing.unit * 4,
        outline: 'none',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    },
});

const GuideListItem = ({
    classes,
    guide,
    intl,
    appId,
    form,
    guideLanguage,
    updateGuide,
    duplicateGuide,
    removeGuide,
    editGuideHost,
}) => {
    const [state, setState] = useState({
        editGuide: false,
        menuAnchorEl: null,
        showGuideDetail: false,
        showActionsMenu: false,
        tabOpened: 0,
        showPublishOptions: false,
        showDeleteDialog: false,
        newGuideFlag: false,
    });

    const dispatch = useDispatch();

    const handleUpdateState = (data) => () => {
        setState((values) => ({ ...values, ...data }));
    };

    useEffect(() => {
        if (guide.isNew) {
            setState((state) => ({
                ...state,
                newGuideFlag: true,
            }));
            setTimeout(() => {
                setState((state) => ({
                    ...state,
                    newGuideFlag: false,
                }));
            }, 1500);
        }
    }, [guide.isNew]);

    const toggleTable = (overrides = {}) => {
        setState((state) => ({
            ...state,
            showGuideDetail: !state.showGuideDetail,
            showPublishOptions: false,
            editGuide: false,
            ...overrides,
        }));
    };

    const closeActionsMenu = () => {
        setState((values) => ({ ...values, showActionsMenu: false, menuAnchorEl: null }));
    };

    const openActionsMenu = (event) => {
        setState((values) => ({ ...values, showActionsMenu: true, anchorEl: event.currentTarget }));
    };

    const onTabChange = (event, tabOpened) => {
        setState((values) => ({ ...values, tabOpened }));
    };

    const handleDuplicateGuide = (guide) => () => {
        dispatch(duplicateGuide(guide));
        closeActionsMenu();
    };

    const handleRemoveGuide = () => {
        setState((values) => ({ ...values, showDeleteDialog: false }));
        dispatch(removeGuide(guide.id));
    };

    const handlePublishGuide = (formData) => () => {
        dispatch(
            editGuideHost(guide.id, formData, {
                success: `${intl.formatMessage({ id: 'Notification.guides.updateHost.success' })}`,
                fail: `${intl.formatMessage({ id: 'Notification.guides.updateHost.fail' })}`,
            })
        );

        setState((values) => ({ ...values, showPublishOptions: false }));
    };

    const handleUpdateGuide = (guide) => () => {
        const formValues = form.getFieldsValue([
            'name',
            'initialUrl',
            'description',
            'sandbox',
            'healthCheck',
            'credentialStorageOption',
            'identity',
            'visitor',
            'startAutomatically',
        ]);

        dispatch(
            updateGuide(
                guide,
                formValues,
                {
                    success: `${intl.formatMessage({ id: 'Notification.guides.update.success' })}`,
                    fail: `${intl.formatMessage({ id: 'Notification.guides.update.fail' })}`,
                },
                {
                    success: `${intl.formatMessage({ id: 'Notification.guidesRules.update.success' })}`,
                    fail: `${intl.formatMessage({ id: 'Notification.guidesRules.update.fail' })}`,
                }
            )
        );

        toggleTable({ showGuideDetail: false });
    };

    const { editGuide, showGuideDetail, tabOpened, showPublishOptions, newGuideFlag } = state;

    return (
        <>
            <Card key={guide.id} className={classnames(classes.guidesBox, { newGuide: newGuideFlag })}>
                <CardContent classes={{ root: classes.CardContentroot }}>
                    {!editGuide && (
                        <GuideListItemContent
                            className={classes}
                            guide={guide}
                            guideLanguage={guideLanguage}
                            showGuideDetail={showGuideDetail}
                            toggleTable={toggleTable}
                        />
                    )}
                    {showPublishOptions && (
                        <PublishGuideForm
                            className={classes.publishForm}
                            guide={guide}
                            onCancel={handleUpdateState({ showPublishOptions: false })}
                            onPublish={handlePublishGuide}
                        />
                    )}

                    {editGuide && (
                        <EditGuideForm
                            intl={intl}
                            guide={guide}
                            onCancel={() => {
                                handleUpdateState({
                                    editGuide: false,
                                    showGuideDetail: false,
                                });
                            }}
                            title={`${intl.formatMessage({
                                id: 'Guides.editForm.title',
                            })}`}
                            form={form}
                        />
                    )}
                    <Collapse in={showGuideDetail} timeout="auto">
                        {!editGuide && (
                            <GuideListItemGeneralTabs
                                tabOpened={tabOpened}
                                onTabChange={onTabChange}
                                intl={intl}
                                showGuideDetail={showGuideDetail}
                                guide={guide}
                                editGuide={editGuide}
                                className={classes}
                                form={form}
                                appId={appId}
                            />
                        )}
                    </Collapse>

                    <GuideFooter
                        intl={intl}
                        guide={guide}
                        form={form}
                        handleUpdateState={handleUpdateState}
                        onDuplicateGuide={handleDuplicateGuide(guide)}
                        handleUpdateGuide={handleUpdateGuide(guide)}
                        closeActionsMenu={closeActionsMenu}
                        openActionsMenu={openActionsMenu}
                        removeGuide={handleRemoveGuide}
                        {...state}
                    />
                </CardContent>
            </Card>
        </>
    );
};

export default withStyles(styles)(createForm()(GuideListItem));
